import { uuid } from './ids';
import type { KVStore } from './storage';

const SESSION_KEY = 'narrato_session';

export interface StoredSession {
  id: string;
  last: number;
  /** How the session arrived: the referrer when it started (sessions saved before 0.1.3 have none). */
  referrer?: string;
}

/**
 * Timeout-based session tracking, matching AnalyticsEvent's session
 * semantics: a session id persists across page loads and renews after
 * `timeoutMs` of inactivity. Every event touch extends the session.
 */
export class SessionManager {
  constructor(
    private store: KVStore,
    private timeoutMs: number,
  ) {}

  /** Returns the current session id, renewing it when expired. */
  current(now: number = Date.now()): string {
    return this.touch(now).id;
  }

  /**
   * Returns the current session, renewing it when expired. A new session
   * records `referrer` as the one it arrived with.
   */
  touch(now: number, referrer = ''): StoredSession {
    const stored = this.read();
    const session =
      stored && now - stored.last <= this.timeoutMs
        ? { ...stored, last: now }
        : { id: uuid(), last: now, referrer };
    this.save(session);
    return session;
  }

  reset(): void {
    this.store.remove(SESSION_KEY);
  }

  private read(): StoredSession | undefined {
    const raw = this.store.get(SESSION_KEY);
    if (!raw) return undefined;
    try {
      const stored = JSON.parse(raw) as StoredSession;
      return typeof stored.id === 'string' ? stored : undefined;
    } catch {
      return undefined; // corrupted entry: start a fresh session
    }
  }

  private save(session: StoredSession): void {
    this.store.set(SESSION_KEY, JSON.stringify(session));
  }
}
